import { Maybe } from '@varo/graphql-schema-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Button from 'shared/components/vxl/Button/Button';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import { Tag, TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface OfferListItemProps extends PressableAnalyticsProps {
  id: string;
  titleText: string;
  subtitleText: string;
  subtitleTextSecondary?: string | undefined;
  distance?: string | undefined;
  originalPrice?: string | undefined;
  discountedPrice?: string | undefined;
  address?: string | undefined;
  tagText?: string | undefined;
  tagStyle?: TagType | undefined;
  tagIconKey?: Maybe<IconKeyType> | undefined;
  iconUrl?: string | undefined;
  onPress?: Maybe<() => void>;
  accessibilityLabel?: string;
  isDisabled: boolean;
  activateOfferAction?: () => void;
  activateOfferAnalyticsId?: string;
  activateOfferCustomAttributes?: Record<string, unknown> | null;
}

const OfferListItem = ({
  id,
  titleText,
  subtitleText,
  subtitleTextSecondary,
  distance,
  originalPrice,
  discountedPrice,
  address,
  tagText = undefined,
  tagStyle = TagType.SUCCESS,
  tagIconKey = undefined,
  iconUrl,
  accessibilityLabel,
  onPress,
  analyticsId = '',
  isDisabled = false,
  activateOfferAction = undefined,
  customAttributes = null,
  activateOfferAnalyticsId = '',
  activateOfferCustomAttributes = null,
}: OfferListItemProps) => {
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'listItem',
  });

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const textLineLimit = 1;

  const listContent = (
    <View style={styles.listItem}>
      <IconAvatar
        variant="MD"
        backgroundColor={theme.color.expressive.orchid}
        iconKey={!iconUrl ? 'STORE' : undefined}
        defaultPlaceholderIcon="STORE"
        imageSource={iconUrl ? { uri: iconUrl } : undefined}
        iconAccessibilityLabel={titleText + ' icon'}
        resizeMode="cover"
      />
      <View style={styles.column}>
        <BodyBaseBoldText
          style={isDisabled ? interactiveStyles.disable : undefined}
          ellipsizeMode="tail"
          numberOfLines={textLineLimit}
          testID="offer-list-titleText"
        >
          {titleText}
        </BodyBaseBoldText>
        {subtitleText ? (
          <BodySmText
            style={isDisabled ? interactiveStyles.disable : undefined}
            ellipsizeMode="tail"
            numberOfLines={textLineLimit}
            testID="offer-list-subtitleText"
          >
            {subtitleText}
          </BodySmText>
        ) : null}

        {subtitleTextSecondary ? (
          <BodySmText
            style={isDisabled ? interactiveStyles.disable : undefined}
            ellipsizeMode="tail"
            numberOfLines={textLineLimit}
            testID="offer-list-subtitleTextSecondary"
          >
            {subtitleTextSecondary}
          </BodySmText>
        ) : null}

        {distance || originalPrice || discountedPrice || address ? (
          <BodyXSText
            numberOfLines={textLineLimit}
            testID="offer-list-tertiaryText"
          >
            {distance ? (
              <BodyXSText
                style={[
                  styles.detailText,
                  isDisabled ? interactiveStyles.disable : undefined,
                ]}
                ellipsizeMode="tail"
                numberOfLines={textLineLimit}
                testID="offer-list-distanceText"
              >
                {` ${distance} `}
              </BodyXSText>
            ) : null}
            {originalPrice ? (
              <BodyXSText
                style={[
                  styles.detailText,
                  isDisabled ? interactiveStyles.disable : undefined,
                ]}
                ellipsizeMode="tail"
                numberOfLines={textLineLimit}
                testID="offer-list-originalPrice"
              >
                {distance ? (
                  <BodyXSText
                    style={[
                      styles.dotText,
                      isDisabled ? interactiveStyles.disable : undefined,
                    ]}
                  >
                    {' • '}
                  </BodyXSText>
                ) : null}
                <BodyXSText
                  style={[
                    styles.originalPrice,
                    isDisabled ? interactiveStyles.disable : undefined,
                  ]}
                  ellipsizeMode="tail"
                  numberOfLines={textLineLimit}
                  testID="offer-list-originalPrice"
                >
                  {` ${originalPrice}`}
                </BodyXSText>
              </BodyXSText>
            ) : null}
            {discountedPrice ? (
              <BodyXSText
                style={[
                  styles.detailText,
                  isDisabled ? interactiveStyles.disable : undefined,
                ]}
                ellipsizeMode="tail"
                numberOfLines={textLineLimit}
                testID="offer-list-discountedPrice"
              >
                {` ${discountedPrice} `}
              </BodyXSText>
            ) : null}
            {address ? (
              <BodyXSText
                style={[
                  styles.detailText,
                  isDisabled ? interactiveStyles.disable : undefined,
                ]}
                ellipsizeMode="tail"
                numberOfLines={textLineLimit}
                testID="offer-list-address"
              >
                {distance || originalPrice ? (
                  <BodyXSText
                    style={[
                      styles.dotText,
                      isDisabled ? interactiveStyles.disable : undefined,
                    ]}
                  >
                    {' • '}
                  </BodyXSText>
                ) : null}
                {address}
              </BodyXSText>
            ) : null}
          </BodyXSText>
        ) : null}
      </View>
      {tagText && tagText.length > 0 ? (
        <View style={styles.tagWrapper}>
          <Tag
            key={`Tag-${tagText}`}
            text={tagText}
            type={tagStyle}
            iconKey={tagIconKey}
          />
        </View>
      ) : null}
      {activateOfferAction ? (
        <View style={styles.buttonContainer}>
          <Button
            onPress={activateOfferAction}
            type="SECONDARY"
            size="SMALL"
            label={translatedText('activateButton', 'Activate')}
            isDisabled={isDisabled}
            analyticsId={activateOfferAnalyticsId}
            customAttributes={activateOfferCustomAttributes}
          />
        </View>
      ) : null}
    </View>
  );

  if (onPress) {
    return (
      <PressableWithDelay
        analyticsId={analyticsId}
        customAttributes={customAttributes}
        disabled={Boolean(isDisabled)}
        aria-disabled={Boolean(isDisabled)}
        accessible
        accessibilityLabel={accessibilityLabel}
        role="button"
        onPress={() => {
          onPress();
          if (isWeb) {
            setIsFocused(false);
          }
        }}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        style={[
          styles.interactiveContainer,
          isFocused && interactiveStyles.focus,
          isHovered && interactiveStyles.hover,
          isPressed && interactiveStyles.press,
          isDisabled && interactiveStyles.disable,
        ]}
        testID="offer-list-item"
      >
        {listContent}
      </PressableWithDelay>
    );
  }
  return (
    <View
      style={[
        styles.interactiveContainer,
        isDisabled && interactiveStyles.disable,
      ]}
      testID="offer-list-item"
      accessible
      accessibilityLabel={accessibilityLabel ?? 'OfferListItem-' + id}
      aria-disabled={Boolean(isDisabled)}
    >
      {listContent}
    </View>
  );
};

const styles = StyleSheet.create({
  column: {
    flexDirection: 'column',
    flex: 1,
  },
  detailText: {
    color: theme.color.text.secondary,
    gap: 2,
  },
  dotText: {
    color: theme.color.text.secondary,
    paddingHorizontal: 2,
  },
  interactiveContainer: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: theme.spacing.m,
    width: '100%',
  },
  listItem: {
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
    gap: theme.spacing.m,
    width: '100%',
  },
  originalPrice: {
    textDecorationLine: 'line-through',
    color: theme.color.text.tertiary,
  },
  buttonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  tagWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
});

const interactiveStyles = StyleSheet.create({
  disable: {
    color: theme.color.interactive.disabled.text,
    backgroundColor: theme.color.interactive.secondary,
  },
  focus: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.interactive.focused.border,
    borderWidth: 1,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
    borderColor: theme.color.interactive.secondaryHovered,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
    borderColor: theme.color.interactive.secondaryPressed,
    borderWidth: 1,
  },
});

export default OfferListItem;
